import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import PanchTatvaButton from '../components/PanchTatvaButton';
import { CartToastContainer, showCartToast } from '../components/CartToast';
import { useCart } from '../context/CartContext';
import products from '../data/products';
import { useStrings } from '../i18n';

const ELEMENT_DESCRIPTIONS = {
  earth: 'Grounded in the richness of soil and stone, Earth products are crafted to nurture and restore.',
  water: 'Fluid and cleansing, Water products move with your rhythm, soothing and refreshing.',
  fire: 'Ignite your spirit. Fire products energise and invigorate with warming, vibrant essences.',
  air: 'Light as breath, Air products carry delicate fragrances that open pathways and invite inspiration.',
  ether: 'Beyond the senses, Ether products invoke clarity, spaciousness, and quiet presence.',
  gift: 'A curated collection of all five elements — a perfect gift of balance and presence.',
};

function ProductCard({ product, strings }) {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);

  const productName =
    product.name ||
    (product.nameKey && strings.products?.[product.nameKey]?.name) || '';
  const productDescription =
    product.description ||
    (product.descriptionKey && strings.products?.[product.descriptionKey]?.description) || '';

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: productName,
      price: product.price,
      quantity,
      image: product.image,
    });
    showCartToast(`${productName} added to cart!`);
  };

  return (
    <div className="bg-surface rounded-2xl shadow-card overflow-hidden">
      {/* Product Image */}
      <div className="bg-cardBg flex items-center justify-center md:p-10">
        <img
          src={product.image}
          alt={productName}
          className="w-full md:max-w-sm object-contain drop-shadow-lg"
          style={{ maxHeight: '420px' }}
          loading="lazy"
          decoding="async"
        />
      </div>

      {/* Product Info */}
      <div className="p-6 md:p-8 space-y-4">
        {/* Category badge */}
        {product.category && (
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-secondary border border-secondary rounded-full px-3 py-1">
            {product.category}
          </span>
        )}

        <h2 className="text-2xl md:text-3xl font-heading text-headingText">
          {productName}
        </h2>

        <p className="text-bodyText leading-relaxed">{productDescription}</p>

        {/* Price */}
        <div className="py-4 border-t border-b border-gray-200">
          <p className="text-3xl font-bold text-secondary">
            {product.currency}{product.price}
          </p>
          <p className="text-xs text-gray-500 mt-1">Inclusive of all taxes</p>
        </div>

        {/* Quantity + Add to Cart */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-bodyText">Qty</label>
            <select
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value))}
              className="px-3 py-2 rounded-lg border-2 border-gray-300 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <PanchTatvaButton onClick={handleAddToCart} className="flex-1 min-w-[160px] py-3">
            {strings.card?.addToCart || 'Add to Cart'}
          </PanchTatvaButton>
        </div>

        {/* Perks */}
        <div className="flex flex-wrap gap-4 pt-2 text-sm text-gray-600">
          <span>✓ Free shipping above ₹500</span>
          <span>✓ 30-day returns</span>
          <span>✓ 100% natural</span>
        </div>
      </div>
    </div>
  );
}

export default function ElementLanding() {
  const { element } = useParams();
  const navigate = useNavigate();
  const strings = useStrings();
  const headerRef = useRef(null);

  const elementKey = element?.toLowerCase();
  const elementProducts = products.filter(
    (p) => p.element?.toLowerCase() === elementKey
  );

  const heroImage = elementProducts.find((p) => p.elementImage)?.elementImage;
  const displayName =
    elementKey ? elementKey.charAt(0).toUpperCase() + elementKey.slice(1) : '';
  const description = ELEMENT_DESCRIPTIONS[elementKey] || `Explore our ${displayName} collection.`;

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.classList.add('show');
    }
  }, [element]);

  if (!elementKey || elementProducts.length === 0) {
    return <Navigate to="/shop" replace />;
  }

  return (
    <div className="w-full">
      <CartToastContainer />

      {/* Element Hero Banner */}
      <section className="relative bg-cardBg overflow-hidden">
        {heroImage && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-25"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
        )}
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 py-14 md:py-20 text-center">
          {/* Breadcrumb */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
            <button onClick={() => navigate('/')} className="hover:text-secondary transition">Home</button>
            <span>•</span>
            <button onClick={() => navigate('/shop')} className="hover:text-secondary transition">Shop</button>
            <span>•</span>
            <span className="text-bodyText font-semibold capitalize">{displayName}</span>
          </div>

          <div ref={headerRef} className="fade-section">
            <h1 className="text-4xl md:text-6xl font-heading text-headingText mb-4">
              {displayName}
            </h1>
            <p className="text-base md:text-lg text-bodyText max-w-2xl mx-auto leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20 space-y-10">
        {elementProducts.map((product) => (
          <ProductCard key={product.id} product={product} strings={strings} />
        ))}
      </section>

      {/* Related Elements */}
      <section className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <h2 className="text-xl md:text-2xl font-heading text-headingText text-center mb-8">
          Explore Other Elements
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {products
            .filter((p) => p.element !== elementKey && p.element)
            .reduce((acc, p) => {
              if (!acc.find((x) => x.element === p.element)) acc.push(p);
              return acc;
            }, [])
            .map((p) => {
              const name = p.name || (p.nameKey && strings.products?.[p.nameKey]?.name) || '';
              return (
                <button
                  key={p.element}
                  onClick={() => navigate(`/element/${p.element}`)}
                  className="bg-cardBg rounded-xl overflow-hidden shadow hover:shadow-lg hover:scale-105 transition text-left"
                >
                  <img
                    src={p.elementImage || p.image}
                    alt={name}
                    className="w-full h-28 object-cover"
                  />
                  <p className="p-3 text-sm font-semibold text-headingText capitalize">{p.element}</p>
                </button>
              );
            })}
        </div>
      </section>
    </div>
  );
}
